import React, { useState } from 'react';
import { Box, Button, TextField, Typography, Drawer } from '@mui/material';
import 'react-quill/dist/quill.snow.css';
import ReactQuill from 'react-quill';

import Slidemenu from '../../components/admin/adminSlidemenu.tsx';
import AdminHeader from '../../components/admin/adminHeader.tsx';

type ProductItem = {
  name: string;
  description: string;
  video: string;
  fileName: string;
};

const AddProductPage: React.FC = () => {
  // State for mobile menu toggle
  const [mobileOpen, setMobileOpen] = useState(false);
  const handleToggleMenu = () => {
    setMobileOpen(prev => !prev);
  };

  const [productName, setProductName] = useState<string>('');
  const [productDescription, setProductDescription] = useState<string>('');
  const [productVideo, setProductVideo] = useState<string>('');
  const [chosenFile, setChosenFile] = useState<File | null>(null);
  const [filePreview, setFilePreview] = useState<string | null>(null);
  const [productTableData, setProductTableData] = useState<ProductItem[]>([]);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];
      setChosenFile(file);
      if (file.type.startsWith('image/')) {
        const reader = new FileReader();
        reader.onloadend = () => {
          setFilePreview(reader.result as string);
        };
        reader.readAsDataURL(file);
      } else {
        setFilePreview(null);
      }
    }
  };

  const handleAddProduct = () => {
    const newProduct: ProductItem = {
      name: productName,
      description: productDescription,
      video: productVideo,
      fileName: chosenFile ? chosenFile.name : '',
    };
    setProductTableData([...productTableData, newProduct]);
    setProductName('');
    setProductDescription('');
    setProductVideo('');
    setChosenFile(null);
    setFilePreview(null);
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100vh' }}>
      {/* Container for sidebar + main content */}
      <Box sx={{ display: 'flex', flex: 1, overflow: 'hidden' }}>
        {/* Sidebar for desktop */}
        <Box
          sx={{
            width: { xs: 0, sm: '250px' },
            flexShrink: 0,
            overflowY: 'auto',
            display: { xs: 'none', sm: 'block' },
          }}
        >
          <Slidemenu mobileOpen={false} onToggle={function (): void {
            throw new Error('Function not implemented.');
          } } />
        </Box>

        {/* Drawer for mobile menu */}
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={handleToggleMenu}
          ModalProps={{
            keepMounted: true,
          }}
          sx={{
            display: { xs: 'block', sm: 'none' },
            '& .MuiDrawer-paper': { width: 250 },
          }}
        >
          <Slidemenu mobileOpen={false} onToggle={function (): void {
            throw new Error('Function not implemented.');
          } } />
        </Drawer>

        {/* Main Content */}
        <Box sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
          {/* AdminHeader with toggle */}
          <AdminHeader onToggleMenu={handleToggleMenu} />

          {/* Content - Fixed mobile centering */}
          <Box
            sx={{
              flexGrow: 1,
              overflow: 'auto',
              padding: { xs: '8px', sm: 2 }, // Reduced mobile padding
              backgroundColor: '#f5f5f5',
              display: 'flex',
              justifyContent: 'center', // Center the content
            }}
          >
            <Box
              sx={{
                width: '100%', // Full width on mobile
                maxWidth: { xs: '100%', sm: 900 }, // No max-width restriction on mobile
                margin: 0, // Remove auto margin
                padding: { xs: 0, sm: 2 }, // No padding on mobile
              }}
            >
              {/* Page title */}
              <Typography
                variant="h4"
                gutterBottom
                sx={{
                  mb: 3,
                  fontSize: { xs: '20px', sm: '28px' },
                  textAlign: 'center',
                  px: { xs: 1, sm: 0 }, // Add horizontal padding only on mobile
                }}
              >
                Add New Product
              </Typography>

              {/* Form Card - Fixed mobile margins */}
              <Box
                sx={{
                  border: '1px solid #ddd',
                  borderRadius: 2,
                  p: { xs: 1.5, sm: 3 }, // Reduced mobile padding
                  mb: 4,
                  backgroundColor: '#fff',
                  width: '100%',
                  mx: { xs: 0, sm: 'auto' }, // No horizontal margin on mobile
                  boxSizing: 'border-box', // Ensure padding doesn't add to width
                }}
              >
                {/* File Button */}
                <Box sx={{ textAlign: 'center', mb: 2 }}>
                  <Button
                    variant="contained"
                    component="label"
                    sx={{
                      backgroundColor: '#C32032',
                      '&:hover': { backgroundColor: '#a01626' },
                      width: { xs: '100%', sm: 200 },
                      height: 40,
                      fontSize: '14px',
                      fontWeight: 600,
                      textTransform: 'uppercase',
                      borderRadius: 1,
                    }}
                  >
                    Choose File
                    <input type="file" hidden onChange={handleFileChange} />
                  </Button>
                </Box>

                {/* Show filename if not image */}
                {chosenFile && !filePreview && (
                  <Typography 
                    sx={{ 
                      mb: 2, 
                      textAlign: 'center',
                      fontSize: { xs: '14px', sm: '16px' },
                      wordBreak: 'break-word',
                      px: { xs: 1, sm: 0 } // Add padding on mobile
                    }} 
                    variant="body2"
                  >
                    Selected File: {chosenFile.name}
                  </Typography>
                )}

                {/* Image preview */}
                {filePreview && (
                  <Box sx={{ mb: 2, mt: 2, textAlign: 'center' }}>
                    <Typography variant="body2" gutterBottom>
                      Image Preview:
                    </Typography>
                    <img
                      src={filePreview}
                      alt="Preview"
                      style={{
                        maxWidth: '100%',
                        height: 'auto',
                        borderRadius: 4,
                        maxHeight: 300,
                        objectFit: 'contain',
                      }}
                    />
                  </Box>
                )}

                {/* Product Name */}
                <TextField
                  label="Product Name"
                  fullWidth
                  sx={{ 
                    mb: 2,
                    '& .MuiInputBase-input': {
                      fontSize: { xs: '14px', sm: '16px' }
                    }
                  }}
                  value={productName}
                  onChange={(e) => setProductName(e.target.value)}
                />

                {/* Product Description */}
                <Typography 
                  gutterBottom 
                  sx={{ 
                    mb: 1,
                    fontSize: { xs: '14px', sm: '16px' }
                  }}
                >
                  Product Description
                </Typography>
                <Box
                  sx={{
                    border: '1px solid #ccc',
                    borderRadius: 2,
                    mb: 2,
                    minHeight: { xs: 120, sm: 150 },
                    width: '100%', // Ensure full width
                    boxSizing: 'border-box',
                    '& .ql-container': {
                      boxSizing: 'border-box',
                    },
                    '& .ql-editor': {
                      minHeight: { xs: '80px', sm: '120px' },
                      fontSize: { xs: '14px', sm: '16px' },
                      boxSizing: 'border-box',
                    },
                    '& .ql-toolbar': {
                      borderBottom: '1px solid #ccc',
                      boxSizing: 'border-box',
                      '& .ql-formats': {
                        marginRight: { xs: '5px', sm: '15px' }
                      }
                    }
                  }}
                >
                  <ReactQuill
                    theme="snow"
                    value={productDescription}
                    onChange={setProductDescription}
                    style={{ 
                      height: window.innerWidth < 600 ? '120px' : '150px',
                      width: '100%',
                      boxSizing: 'border-box'
                    }}
                  />
                </Box>

                {/* Upload Video Button */}
                <Box sx={{ textAlign: 'center', mb: 2 }}>
                  <Button
                    variant="contained"
                    sx={{
                      backgroundColor: '#C32032',
                      '&:hover': { backgroundColor: '#a01626' },
                      width: { xs: '100%', sm: 202 },
                      height: 40,
                      fontSize: '14px',
                      fontWeight: 600,
                      textTransform: 'uppercase',
                      borderRadius: 1,
                    }}
                    onClick={() => alert('Upload video functionality')}
                  >
                    Upload Product Video
                  </Button>
                </Box>

                {/* Additional input example */}
                <TextField
                  label="Additional Field"
                  fullWidth
                  sx={{ 
                    mb: 2,
                    '& .MuiInputBase-input': {
                      fontSize: { xs: '14px', sm: '16px' }
                    }
                  }}
                />

                {/* Save Button */}
                <Box sx={{ textAlign: 'center', mt: 3 }}>
                  <Button
                    variant="contained"
                    color="primary"
                    sx={{ 
                      width: { xs: '100%', sm: 200 },
                      height: 45,
                      fontSize: '15px',
                      fontWeight: 600,
                      textTransform: 'uppercase',
                      borderRadius: 1,
                      backgroundColor: '#1976d2',
                      '&:hover': { backgroundColor: '#1565c0' },
                    }}
                    onClick={handleAddProduct}
                  >
                    Save Product
                  </Button>
                </Box>
              </Box>

              {/* List of added products - Mobile Responsive */}
              <Box sx={{ mt: 4, px: { xs: 0, sm: 0 } }}>
                <Typography 
                  variant="h6" 
                  gutterBottom 
                  sx={{ 
                    mb: 2, 
                    textAlign: 'center',
                    fontSize: { xs: '18px', sm: '20px' },
                    px: { xs: 1, sm: 0 }
                  }}
                >
                  Product List
                </Typography>
                {productTableData.length === 0 ? (
                  <Typography 
                    sx={{ 
                      textAlign: 'center',
                      fontSize: { xs: '14px', sm: '16px' },
                      px: { xs: 1, sm: 0 }
                    }}
                  >
                    No products added yet.
                  </Typography>
                ) : (
                  <Box
                    sx={{
                      display: 'grid',
                      gridTemplateColumns: { 
                        xs: '1fr', 
                        sm: 'repeat(auto-fit, minmax(300px, 1fr))' 
                      },
                      gap: { xs: 1, sm: 2 },
                      width: '100%',
                    }}
                  >
                    {productTableData.map((item, index) => (
                      <Box
                        key={index}
                        sx={{
                          p: { xs: 1.5, sm: 2 },
                          border: '1px solid #ddd',
                          borderRadius: 1,
                          backgroundColor: '#fff',
                          width: '100%',
                          boxSizing: 'border-box',
                          '& > *': {
                            mb: { xs: 0.5, sm: 1 }
                          }
                        }}
                      >
                        <Typography 
                          sx={{ 
                            fontSize: { xs: '14px', sm: '16px' },
                            wordBreak: 'break-word'
                          }}
                        >
                          <strong>Name:</strong> {item.name}
                        </Typography>
                        <Typography 
                          sx={{ 
                            fontSize: { xs: '14px', sm: '16px' },
                            wordBreak: 'break-word',
                            '& p': { margin: 0 },
                            '& div': { margin: 0 }
                          }}
                        >
                          <strong>Description:</strong>
                          <Box 
                            component="div" 
                            dangerouslySetInnerHTML={{ __html: item.description }}
                            sx={{
                              '& *': {
                                fontSize: { xs: '13px !important', sm: '15px !important' },
                                margin: '0 !important',
                                padding: '0 !important'
                              }
                            }}
                          />
                        </Typography>
                        <Typography 
                          sx={{ 
                            fontSize: { xs: '14px', sm: '16px' },
                            wordBreak: 'break-word'
                          }}
                        >
                          <strong>Video:</strong> {item.video || 'Not uploaded'}
                        </Typography>
                        {item.fileName && (
                          <Typography
                            sx={{ 
                              fontSize: { xs: '14px', sm: '16px' },
                              wordBreak: 'break-word'
                            }}
                          >
                            <strong>File:</strong> {item.fileName}
                          </Typography>
                        )}
                      </Box>
                    ))}
                  </Box>
                )}
              </Box>
            </Box>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default AddProductPage;